import { getCurrencyFormatter } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import ChevronDown from "@redotech/redo-icons/chevron-down.svg";
import ChevronUp from "@redotech/redo-icons/chevron-up.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { FulfillmentGroupPickStatus } from "@redotech/redo-model/fulfillments/fulfillment-group";
import type { Fulfillment, LineItem } from "@redotech/redo-model/order";
import type { ShippingLine } from "@redotech/redo-model/shopify-order";
import {
  RedoBadge,
  RedoBadgeColor,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { CopyableText } from "@redotech/redo-web/copyable-text";
import { Flex } from "@redotech/redo-web/flex";
import { Icon } from "@redotech/redo-web/icon";
import ExternalLinkIcon from "@redotech/redo-web/icon-old/external-link.svg";
import { Text } from "@redotech/redo-web/text";
import { unique } from "@redotech/util/array";
import { getDateString } from "@redotech/util/date-format";
import { formatSentenceCase } from "@redotech/util/string";
import { assertNever } from "@redotech/util/type";
import { memo, useState } from "react";
import { formatShippingAddressString } from "../../order/format-shipping-address";
import { orderExternalUrl } from "../../return/util";
import type { OrderWithPickStatus } from "../db/collections";
import * as styles from "./shared-card-components.module.css";

export interface OrderCardProps {
  order: OrderWithPickStatus;
}

export const OrderCard = memo(function OrderCard({ order }: OrderCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const team = useRequiredContext(TeamContext);

  const handleToggle = useHandler(() => {
    setIsExpanded((prev) => !prev);
  });

  const orderName = order.shopify.name;
  const orderDate = new Date(order.shopifyCreatedAt);
  const totalPrice = order.shopify.total_price_usd || order.shopify.total_price;
  const currency = order.shopify.presentment_currency;
  const externalUrl = orderExternalUrl(team.storeUrl, order);

  return (
    <Flex
      bgColor="secondary"
      borderColor="primary"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="md"
      p="md"
      radius="md"
    >
      <Flex align="center" gap="xs" justify="space-between">
        <Flex align="center" gap="xxs" justify="center">
          <CopyableText
            altTextToCopy={
              orderName.startsWith("#") ? orderName.slice(1) : orderName
            }
            fontSize="xs"
            fontWeight="medium"
          >
            {`Order ${orderName}`}
          </CopyableText>
          {externalUrl && (
            <RedoButton
              hierarchy="tertiary"
              IconLeading={() => (
                <Icon
                  arbiterIconSvg={ExternalLinkIcon}
                  color="ghost"
                  size="extra-small"
                />
              )}
              onClick={() => {
                window.open(externalUrl, "_blank");
              }}
              size="md"
            />
          )}
        </Flex>

        <RedoButton
          hierarchy="tertiary"
          IconLeading={() => (
            <Icon
              arbiterIconSvg={isExpanded ? ChevronUp : ChevronDown}
              color="ghost"
              size="extra-small"
            />
          )}
          onClick={handleToggle}
          size="xs"
        />
      </Flex>

      <OrderBadges order={order} />

      <Flex dir="column" gap="md">
        <Flex align="center" gap="md" justify="space-between">
          <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
            Ordered on
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {getDateString(orderDate)}
          </Text>
        </Flex>

        <Flex align="center" gap="md" justify="space-between">
          <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
            Total
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {getCurrencyFormatter(currency).format(totalPrice)}
          </Text>
        </Flex>
      </Flex>

      {isExpanded && (
        <Flex dir="column" gap="md">
          <OrderShippingMethod order={order} />

          <OrderFulfillmentDetails order={order} />

          <Flex
            borderColor="secondary"
            borderStyle="solid"
            borderTopWidth="1px"
            dir="column"
            gap="md"
            pt="md"
          >
            <Text fontSize="xs" fontWeight="semibold" textColor="primary">
              Items
            </Text>
            {order.shopify.line_items.map((lineItem) => (
              <OrderLineItem
                currency={currency}
                key={lineItem.id}
                lineItem={lineItem}
              />
            ))}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
});

const getPickStatusBadge = (
  status: FulfillmentGroupPickStatus,
): { label: string; color: RedoBadgeColor } => {
  switch (status) {
    case FulfillmentGroupPickStatus.PROBLEM:
      return { label: "Picking problem", color: "error" };
    case FulfillmentGroupPickStatus.IN_PROGRESS:
      return { label: "Picking", color: "warning" };
    case FulfillmentGroupPickStatus.PICKED:
      return { label: "Picked", color: "success" };
    default:
      return assertNever(status);
  }
};

const OrderBadges = memo(function OrderBadges({
  order,
}: {
  order: OrderWithPickStatus;
}) {
  const pickStatuses = order.pickStatuses || [];
  const uniquePickStatuses = Array.from(new Set(pickStatuses));

  const isUnfulfilled = !order.shopify?.fulfillment_status;
  const hasPickStatuses = uniquePickStatuses.length > 0;

  // Only show "Unfulfilled" badge if the order is unfulfilled AND all pick statuses are PICKED
  // Otherwise, the picking status (like "Picking" or "Picking problem") already implies unfulfilled
  const allPicked =
    hasPickStatuses &&
    uniquePickStatuses.every(
      (status) => status === FulfillmentGroupPickStatus.PICKED,
    );
  const shouldShowUnfulfilledBadge = isUnfulfilled && allPicked;

  return (
    <Flex gap="xs" wrap="wrap">
      {order.shopify?.financial_status && (
        <RedoBadge
          color={
            order.shopify.financial_status === "paid" ? "success" : "error"
          }
          size="sm"
          text={formatSentenceCase(order.shopify.financial_status)}
        />
      )}
      {order.shopify?.fulfillment_status && (
        <RedoBadge
          color={
            order.shopify.fulfillment_status === "fulfilled"
              ? "success"
              : "warning"
          }
          size="sm"
          text={formatSentenceCase(order.shopify.fulfillment_status)}
        />
      )}
      {shouldShowUnfulfilledBadge && (
        <RedoBadge color="warning" size="sm" text="Unfulfilled" />
      )}
      {order.shopify?.cancelled_at && (
        <RedoBadge color="error" size="sm" text="Canceled" />
      )}
      {order.protected && (
        <RedoBadge color="blueLight" size="sm" text="Returns Coverage" />
      )}
      {order.packageProtected && (
        <RedoBadge color="blueLight" size="sm" text="Package Protection" />
      )}
      {hasPickStatuses &&
        uniquePickStatuses.map((status) => {
          const badge = getPickStatusBadge(status);
          return (
            <RedoBadge
              color={badge.color}
              key={status}
              size="sm"
              text={badge.label}
            />
          );
        })}
    </Flex>
  );
});

const OrderShippingMethod = memo(function OrderShippingMethod({
  order,
}: {
  order: OrderWithPickStatus;
}) {
  const shippingLines = order.shopify?.shipping_lines || [];
  if (shippingLines.length === 0) {
    return null;
  }

  const uniqueShippingLines = unique(
    shippingLines,
    (line: ShippingLine) => line.title,
  );

  const currency = order.shopify.presentment_currency;

  return (
    <Flex
      borderColor="secondary"
      borderStyle="solid"
      borderTopWidth="1px"
      dir="column"
      gap="md"
      pt="md"
    >
      <Text fontSize="xs" fontWeight="semibold" textColor="primary">
        {uniqueShippingLines.length === 1
          ? "Shipping method"
          : "Shipping methods"}
      </Text>
      <Flex dir="column" gap="sm">
        {uniqueShippingLines.map((line, index) => (
          <Flex align="center" gap="md" justify="space-between" key={index}>
            <CopyableText fontSize="xs" textColor="primary">
              {line.title}
            </CopyableText>
            <Text fontSize="xs" fontWeight="medium" textColor="primary">
              {getCurrencyFormatter(currency).format(Number(line.price))}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
});

const getShipmentStatusBadgeColor = (
  status: string | null | undefined,
): RedoBadgeColor => {
  if (!status) return "warning";

  const lowerStatus = status.toLowerCase();
  if (lowerStatus === "delivered" || lowerStatus === "fulfilled") {
    return "success";
  }
  if (lowerStatus === "shipped" || lowerStatus === "in_transit") {
    return "blueLight";
  }
  return "warning";
};

const OrderFulfillmentDetails = memo(function OrderFulfillmentDetails({
  order,
}: {
  order: OrderWithPickStatus;
}) {
  const fulfillments = order.shopify?.fulfillments || [];

  if (fulfillments.length === 0) {
    return (
      <Flex
        borderColor="secondary"
        borderStyle="solid"
        borderTopWidth="1px"
        dir="column"
        gap="md"
        pt="md"
      >
        <Text fontSize="xs" fontWeight="semibold" textColor="primary">
          Fulfillment
        </Text>
        <Text fontSize="xs" textColor="tertiary">
          No fulfillments yet
        </Text>
      </Flex>
    );
  }

  const shippingAddress = formatShippingAddressString(
    order.shopify.shipping_address,
  );

  return (
    <Flex
      borderColor="secondary"
      borderStyle="solid"
      borderTopWidth="1px"
      dir="column"
      gap="md"
      pt="md"
    >
      <Text fontSize="xs" fontWeight="semibold" textColor="primary">
        Fulfillment
      </Text>
      {fulfillments.map((fulfillment: Fulfillment, index: number) => (
        <Flex dir="column" gap="md" key={index}>
          {fulfillments.length > 1 && (
            <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
              Package {index + 1}
            </Text>
          )}

          {fulfillment.shipment_status && (
            <Flex>
              <RedoBadge
                color={getShipmentStatusBadgeColor(fulfillment.shipment_status)}
                size="sm"
                text={formatSentenceCase(fulfillment.shipment_status)}
              />
            </Flex>
          )}

          {fulfillment.tracking_number && (
            <Flex dir="column" gap="xs">
              <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
                Tracking
              </Text>
              <Flex align="center" gap="xs">
                <CopyableText fontSize="xs" textColor="primary">
                  {fulfillment.tracking_number}
                </CopyableText>
                {fulfillment.tracking_url && (
                  <RedoButton
                    hierarchy="tertiary"
                    IconLeading={() => (
                      <Icon
                        arbiterIconSvg={ExternalLinkIcon}
                        color="ghost"
                        size="compact"
                      />
                    )}
                    onClick={() => {
                      window.open(
                        fulfillment.tracking_url ?? undefined,
                        "_blank",
                      );
                    }}
                    size="xs"
                  />
                )}
              </Flex>
            </Flex>
          )}

          {shippingAddress && (
            <Flex dir="column" gap="xs">
              <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
                Shipping address
              </Text>
              <CopyableText
                fontSize="xs"
                style={{ whiteSpace: "pre-line" }}
                textColor="primary"
              >
                {shippingAddress}
              </CopyableText>
            </Flex>
          )}
        </Flex>
      ))}
    </Flex>
  );
});

const OrderLineItem = memo(function OrderLineItem({
  lineItem,
  currency,
}: {
  lineItem: LineItem;
  currency: string;
}) {
  return (
    <Flex align="flex-start" gap="sm">
      {lineItem.image?.src && (
        <Flex
          borderColor="secondary"
          borderStyle="solid"
          borderWidth="1px"
          className={styles.imageContainer}
          radius="sm"
          shrink="0"
        >
          <img
            alt={lineItem.name}
            className={styles.image}
            src={lineItem.image.src ?? ""}
          />
        </Flex>
      )}

      <Flex dir="column" gap="xs" grow="1">
        <Flex align="flex-start" gap="sm" justify="space-between">
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {lineItem.name}
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {getCurrencyFormatter(currency).format(Number(lineItem.price))}
          </Text>
        </Flex>

        <Flex align="center" gap="sm">
          {lineItem.variant_title && (
            <Text fontSize="xs" textColor="tertiary">
              {lineItem.variant_title}
            </Text>
          )}
          <Text fontSize="xs" textColor="tertiary">
            Qty: {lineItem.quantity}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
});
